import React, { useEffect, useRef, useState } from "react";
import { useDismissible } from "../native/dismissibleStack";
import { TRUST_LABEL, venueNames } from "./safeSpotTokens";
import { hoursCopy } from "./safeSpotOpenState";
import { formatDistance } from "./safeSpotDistance";
import {
  acceptedMeetupTrades,
  chatHandoffPath,
  counterpartLabel,
  mapsSearchUrl,
} from "./safeSpotHandoff";
import { VenueGlyph } from "./SafeSpotMarker";
import "./safe-spot.css";

const Icon = ({ children }) => (
  <svg className="ss-icon" viewBox="0 0 24 24" aria-hidden="true">
    {children}
  </svg>
);

/**
 * Venue detail sheet from the Safe Spot spec.
 *
 * Sits in the map page overlay, not inside a Mapbox Marker — a transform on
 * the marker would drag the sheet around the map. Suggest hands off into an
 * accepted trade's chat; it does not invent a meetup channel of its own.
 */
const SafeSpotSheet = ({
  spot,
  lang = "en",
  dir,
  distanceM = null,
  myId,
  trades = null,
  tradesLoading = false,
  tradesError = false,
  onClose,
  onNavigate,
}) => {
  const [choosing, setChoosing] = useState(false);
  useDismissible(Boolean(spot), onClose, "safe-spot-sheet");

  // Focus in on open, back to the pin on close (E2E audit 18·8). The sheet
  // had neither: opening left focus on the pin, and its own Close handle
  // unmounted the focused button, dropping focus to <body>. The same two
  // effects as CollectorPreviewSheet, split the same way and for the same
  // reasons (see the note there): the opener belongs to the sheet being OPEN,
  // the re-focus to which venue is IN it, so switching venues neither
  // restores focus mid-gesture nor freezes the opener on the first pin.
  const sheetRef = useRef(null);
  const openerRef = useRef(null);
  const isOpen = Boolean(spot);
  const spotKey = spot ? String(spot._id ?? "") : null;

  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener && opener !== document.body && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const invoker = document.activeElement;
    const sheet = sheetRef.current;
    if (invoker && invoker !== document.body && !(sheet && sheet.contains(invoker))) {
      openerRef.current = invoker;
    }
    sheet?.focus({ preventScroll: true });
  }, [isOpen, spotKey]);

  if (!spot) return null;

  const { primary, secondary } = venueNames(spot, lang);
  const hours = hoursCopy(spot);
  const distance = formatDistance(distanceM);
  const maps = mapsSearchUrl(spot);
  const accepted = acceptedMeetupTrades(trades);
  const sheetDir = dir || (lang === "he" ? "rtl" : "ltr");

  const goTo = (trade) => {
    if (!trade || !myId) return;
    onNavigate?.(chatHandoffPath(trade, myId, spot._id));
  };

  const suggest = () => {
    if (tradesLoading) return;
    if (accepted.length === 1) {
      goTo(accepted[0]);
      return;
    }
    if (accepted.length > 1) {
      setChoosing(true);
      return;
    }
    setChoosing(false);
  };

  const noneReady = !tradesLoading && !tradesError && trades && accepted.length === 0;

  return (
    <section
      ref={sheetRef}
      // -1: the root holds focus without becoming a Tab stop of its own.
      tabIndex={-1}
      className="ss-sheet"
      dir={sheetDir}
      role="dialog"
      aria-label={primary}
      data-testid="safe-spot-sheet"
    >
      <button
        type="button"
        className="ss-handle-hit"
        onClick={onClose}
        aria-label="Close"
        data-testid="safe-spot-sheet-close"
      >
        <span className="ss-handle" />
      </button>

      <div className="ss-head">
        <div className="ss-tile" aria-hidden="true">
          <VenueGlyph category={spot.category} />
        </div>
        <div className="ss-names">
          <div className="ss-name-primary">{primary}</div>
          {secondary ? <div className="ss-name-secondary">{secondary}</div> : null}
        </div>
        {distance ? (
          <span className="ss-distance">
            <Icon>
              <path d="m3 11 19-9-9 19-2-8z" />
            </Icon>
            {distance} from you
          </span>
        ) : null}
      </div>

      <div className="ss-trust">
        <Icon>
          <path d="m11 17 2 2a1 1 0 1 0 3-3" />
          <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
          <path d="m21 3 1 11h-2" />
          <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
          <path d="M3 4h8" />
        </Icon>
        <span>{TRUST_LABEL}</span>
      </div>

      <div className="ss-rows">
        {spot.address ? (
          <div className="ss-row">
            <Icon>
              <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
              <circle cx="12" cy="10" r="3" />
            </Icon>
            <div>{spot.address}</div>
          </div>
        ) : null}
        <div className="ss-row">
          <Icon>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </Icon>
          <div>
            <span
              className={
                hours.tone === "open"
                  ? "ss-open"
                  : hours.tone === "closed"
                  ? "ss-closed"
                  : undefined
              }
            >
              {hours.headline}
            </span>
            {hours.sub ? <div className="ss-row-sub">{hours.sub}</div> : null}
          </div>
        </div>
      </div>

      {spot.note ? (
        <div className="ss-note">
          <Icon>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </Icon>
          <div>{spot.note}</div>
        </div>
      ) : null}

      <div className="ss-actions">
        {tradesError ? (
          <p className="ss-row-sub">Could not load your trades just now.</p>
        ) : null}
        {noneReady ? (
          <p className="ss-row-sub" data-testid="safe-spot-no-trade">
            You need an accepted trade before you can suggest a meeting place.
          </p>
        ) : null}

        {choosing && accepted.length > 1 ? (
          <div data-testid="safe-spot-trade-chooser">
            {accepted.map((trade) => (
              <button
                key={trade._id}
                type="button"
                className="ss-btn ss-btn--quiet"
                onClick={() => goTo(trade)}
              >
                {counterpartLabel(trade, myId)}
              </button>
            ))}
          </div>
        ) : (
          <button
            type="button"
            className="ss-btn ss-btn--primary"
            onClick={suggest}
            disabled={tradesLoading || noneReady || tradesError}
            data-testid="safe-spot-suggest"
          >
            <Icon>
              <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
              <circle cx="12" cy="10" r="3" />
            </Icon>
            Suggest as meeting place
          </button>
        )}

        {maps ? (
          <a
            className="ss-btn ss-btn--quiet"
            href={maps}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="safe-spot-open-maps"
          >
            <Icon>
              <path d="M15 3h6v6" />
              <path d="M10 14 21 3" />
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            </Icon>
            Open in Maps
          </a>
        ) : null}
      </div>
    </section>
  );
};

export default SafeSpotSheet;
